import { ArrowLeft, ChevronDown, ChevronRight, Copy, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { LogEntry, LogLevel, LogsPayload } from '../api/types';
import { ScreenLink } from '../components/ScreenLink';
import { clock, dayHeader } from '../lib/format';
import { readParams, withParam, writeUrl } from '../lib/urlState';

/**
 * sphere-watch's own log — what `docker logs sphere_watch` shows, without a
 * shell on the watch VPS.
 *
 * It lives under Systems because it is about the monitor rather than about
 * anything monitored: why a probe could not observe, what a run actually read,
 * which variable is unset. Each check that goes blind says so here once, with
 * its reason, and again only if it is still blind a quarter of an hour later;
 * the two tenant-activity checks write one line per run naming which tenants
 * they read and which they could not.
 *
 * Newest first, like the Audit trail beside it. "Live" tails by asking only for
 * what arrived after the newest line already on screen, so the poll costs a
 * handful of rows, not a page.
 */

/** The level select, least to most inclusive, as the API's `level` means "this and worse". */
export const LEVELS: { value: LogLevel; label: string }[] = [
  { value: 'error', label: 'Errors only' },
  { value: 'warn', label: 'Warnings and errors' },
  { value: 'info', label: 'Everything' },
  { value: 'debug', label: 'Everything, with debug' },
];

/** A level from the URL, or the default. Anything unrecognised is the default, not an error. */
export function parseLevel(value: string | null): LogLevel {
  return LEVELS.some((option) => option.value === value) ? (value as LogLevel) : 'info';
}

/**
 * The first line of a message, and whatever follows it.
 *
 * Stack traces arrive as one message of many lines; collapsed to the first, a
 * page of errors reads as a list of errors rather than a wall of frames.
 */
export function splitMessage(message: string, limit = 280): { head: string; rest: string | null } {
  const newline = message.indexOf('\n');
  const head = newline === -1 ? message : message.slice(0, newline);
  if (head.length > limit) return { head: `${head.slice(0, limit)}…`, rest: message };
  return { head, rest: newline === -1 ? null : message };
}

/** What the screen keeps, so a tab left on Live for a day does not grow without end. */
export const MAX_KEPT = 2000;

/**
 * Lines that arrived since the newest on screen, merged in.
 *
 * By id, newest first, without duplicates: a tail that overlaps the page it
 * extends — a retried poll, two in flight across a filter change — must not
 * draw a line twice.
 */
export function mergeNewest(current: LogEntry[], incoming: LogEntry[]): LogEntry[] {
  const seen = new Set(current.map((entry) => entry.id));
  const fresh = incoming.filter((entry) => entry.id !== null && !seen.has(entry.id));
  if (!fresh.length) return current;
  return [...fresh, ...current].sort((a, b) => (b.id ?? 0) - (a.id ?? 0)).slice(0, MAX_KEPT);
}

const LIVE_MS = 5_000;
const PAGE = 200;

function Line({ entry, showInstance }: { entry: LogEntry; showInstance: boolean }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { head, rest } = splitMessage(entry.message);
  const copy = async () => {
    await navigator.clipboard.writeText(entry.message);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className={`wt-log wt-log-${entry.level}`}>
      <span className="wt-log-time" title={`${entry.at}${entry.instance ? ` · ${entry.instance}` : ''}`}>
        {clock(entry.at)}
      </span>
      <span className={`wt-log-level ${entry.level}`}>{entry.level}</span>
      <div className="wt-log-msg">
        {rest ? (
          <button type="button" className="wt-log-toggle" onClick={() => setOpen((value) => !value)}>
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>{head}</span>
          </button>
        ) : (
          <span>{head}</span>
        )}
        {open && rest && (
          <div className="wt-log-detail">
            <pre>{rest}</pre>
            <button type="button" className="btn btn-ghost" onClick={() => void copy()}>
              <Copy size={13} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>
      {showInstance && <span className="wt-log-instance">{entry.instance}</span>}
    </div>
  );
}

export function Logs() {
  const [level, setLevel] = useState<LogLevel>(() => parseLevel(readParams().get('level')));
  const [search, setSearch] = useState(() => readParams().get('q') ?? '');
  const [applied, setApplied] = useState(search);
  const [live, setLive] = useState(true);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [persisted, setPersisted] = useState(true);
  const [note, setNote] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Bumped on every filter change, so a tail or a page requested under the old
  // filter cannot land in the list built for the new one.
  const generation = useRef(0);
  const tailing = useRef(false);

  const query = useMemo(() => {
    const params = new URLSearchParams({ level, limit: String(PAGE) });
    if (applied) params.set('q', applied);
    return params.toString();
  }, [level, applied]);

  // Typing settles before it searches, rather than querying per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setApplied(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // The URL is the state, so a Systems link — or a pasted one — opens on the
  // same filter.
  useEffect(() => {
    let params = withParam(readParams(), 'level', level === 'info' ? null : level);
    params = withParam(params, 'q', applied || null);
    writeUrl(params);
  }, [level, applied]);

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true);
    api
      .get<LogsPayload>(`/logs?${query}`)
      .then((page) => {
        if (mine !== generation.current) return;
        setEntries(page.entries);
        setHasMore(page.hasMore);
        setPersisted(page.persisted);
        setNote(page.note ?? null);
        setError(null);
      })
      .catch((caught: Error) => {
        if (mine === generation.current) setError(caught);
      })
      .finally(() => {
        if (mine === generation.current) setLoading(false);
      });
  }, [query]);

  // Read by the tail's interval, which must not be re-created on every line.
  const newestId = entries.find((entry) => entry.id !== null)?.id ?? null;
  const newestRef = useRef(newestId);
  useEffect(() => {
    newestRef.current = newestId;
  }, [newestId]);

  useEffect(() => {
    if (!live) return;
    const timer = setInterval(async () => {
      if (tailing.current) return;
      tailing.current = true;
      const mine = generation.current;
      try {
        const after = newestRef.current;
        if (persisted && after !== null) {
          const page = await api.get<LogsPayload>(`/logs?${query}&after=${after}&limit=500`);
          if (mine !== generation.current) return;
          // More than a poll's worth arrived: start again from the newest page
          // rather than draw a list with a hole in the middle of it.
          if (page.hasMore) {
            const fresh = await api.get<LogsPayload>(`/logs?${query}`);
            if (mine === generation.current) setEntries(fresh.entries);
          } else {
            setEntries((current) => mergeNewest(current, page.entries));
          }
          setPersisted(page.persisted);
        } else {
          // The in-memory fallback has no ids to tail from, and an empty list
          // has nothing to tail after: re-read the newest page.
          const page = await api.get<LogsPayload>(`/logs?${query}`);
          if (mine !== generation.current) return;
          setEntries(page.entries);
          setHasMore(page.hasMore);
          setPersisted(page.persisted);
          setNote(page.note ?? null);
        }
        setError(null);
      } catch (caught) {
        if (mine === generation.current) setError(caught as Error);
      } finally {
        tailing.current = false;
      }
    }, LIVE_MS);
    return () => clearInterval(timer);
  }, [live, query, persisted]);

  const loadOlder = async () => {
    const oldest = entries.at(-1)?.id;
    if (!oldest) return;
    const mine = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.get<LogsPayload>(`/logs?${query}&before=${oldest}`);
      if (mine !== generation.current) return;
      setEntries((current) => [...current, ...page.entries]);
      setHasMore(page.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  const groups = useMemo(() => {
    const out: [string, LogEntry[]][] = [];
    for (const entry of entries) {
      const header = dayHeader(entry.at);
      const last = out[out.length - 1];
      if (last && last[0] === header) last[1].push(entry);
      else out.push([header, [entry]]);
    }
    return out;
  }, [entries]);

  // Which process said it matters only once there is more than one: two
  // instances, or a container that restarted inside the window.
  const showInstance = useMemo(() => new Set(entries.map((entry) => entry.instance)).size > 1, [entries]);

  return (
    <>
      <div className="wt-head">
        <h1>Logs</h1>
        <span className="sub">sphere-watch’s own log, newest first</span>
        <span className="wt-spacer" />
        <ScreenLink to="/systems" previewTab="systems" className="btn btn-secondary">
          <ArrowLeft size={14} /> Systems
        </ScreenLink>
      </div>

      <div className="wt-filters">
        <select
          className="select"
          aria-label="Level"
          value={level}
          onChange={(event) => setLevel(parseLevel(event.target.value))}
        >
          {LEVELS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="wt-search">
          <Search size={14} />
          <input
            className="input"
            type="search"
            placeholder="Check id, tenant, error…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="toggle wt-log-live">
          <input type="checkbox" checked={live} onChange={(event) => setLive(event.target.checked)} />
          <span className="toggle-track" />
          <span>Live</span>
        </label>
        <span className="wt-when">
          {live ? 'new lines appear at the top every few seconds' : 'paused — new lines are not fetched'}
        </span>
      </div>

      {!persisted && (
        <div className="wt-banner wt-banner-warn">
          {note || 'The persisted log could not be read; this is one process’s memory.'}
        </div>
      )}
      {error && <div className="wt-banner wt-banner-danger">{error.message}</div>}

      {loading && !entries.length ? (
        <div className="wt-empty">Loading…</div>
      ) : !entries.length ? (
        <div className="wt-empty">
          <div className="big">{applied || level !== 'info' ? 'Nothing matches.' : 'Nothing logged yet.'}</div>
          <div>
            {applied || level !== 'info'
              ? 'Try a wider level or a different search.'
              : 'Lines appear here as sphere-watch writes them.'}
          </div>
        </div>
      ) : (
        <>
          {groups.map(([header, lines]) => (
            <section key={header}>
              <div className="wt-daygroup">{header}</div>
              <div className="wt-logs">
                {lines.map((entry, index) => (
                  <Line
                    key={entry.id ?? `${entry.at}-${index}`}
                    entry={entry}
                    showInstance={showInstance}
                  />
                ))}
              </div>
            </section>
          ))}
          <div className="wt-more">
            {hasMore && persisted ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={loadingMore}
                onClick={() => void loadOlder()}
              >
                {loadingMore ? 'Loading…' : `Load ${PAGE} older`}
              </button>
            ) : null}
            <span className="wt-when">
              {entries.length} line(s){hasMore ? ', newest first' : ' — that is everything kept for this filter'}
            </span>
          </div>
        </>
      )}
    </>
  );
}
